import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useTenants } from "@/lib/queries";
import {
  KINDS, dateInputValue, publishedAtFor, useCreateSystemUpdate, useUpdateSystemUpdate,
  type SystemUpdate, type SystemUpdateInput, type SystemUpdateKind,
} from "@/lib/system-updates";

/**
 * Post a system update, or edit one (ACL-631): its kind, a title, a line or two, and the day it is
 * dated. A superadmin also says who reads it — every tenant, or one — which an admin never picks:
 * theirs is always their own tenant's, and the server holds them to it.
 */

const TITLE_MAX = 160;
const BODY_MAX = 2000;
/** The Select's value for "every tenant": a Select item cannot be empty. */
const EVERYONE = "everyone";

const schema = z.object({
  kind: z.enum(["feature", "improvement", "fix"]),
  title: z.string().trim().min(1, "Required").max(TITLE_MAX, `At most ${TITLE_MAX} characters`),
  body: z.string().trim().min(1, "Required").max(BODY_MAX, `At most ${BODY_MAX} characters`),
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a day"),
  audience: z.string(),
});
type FormValues = z.infer<typeof schema>;

const blank = (): FormValues => ({
  kind: "feature", title: "", body: "", day: dateInputValue(undefined), audience: EVERYONE,
});

export function SystemUpdateFormDialog({
  open, onOpenChange, update, isSuperadmin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for a new one. */
  update?: SystemUpdate;
  isSuperadmin: boolean;
}) {
  const toast = useToast();
  const create = useCreateSystemUpdate();
  const save = useUpdateSystemUpdate();
  const { data: tenants } = useTenants({ enabled: isSuperadmin && open });
  const [formError, setFormError] = React.useState<string | null>(null);
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: blank() });
  const { errors } = form.formState;
  const changed = useFormChanged(form);
  const values = form.watch();

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    form.reset(
      update
        ? {
            kind: update.kind,
            title: update.title,
            body: update.body,
            day: dateInputValue(update.published_at),
            audience: update.tenant_id === null ? EVERYONE : String(update.tenant_id),
          }
        : blank(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, update]);

  const onSubmit = form.handleSubmit(async (submitted) => {
    setFormError(null);
    const input: SystemUpdateInput = {
      kind: submitted.kind,
      title: submitted.title,
      body: submitted.body,
    };
    try {
      if (update) {
        // The date moves only when somebody moved it: an untouched day keeps its time.
        if (submitted.day !== dateInputValue(update.published_at)) {
          input.published_at = publishedAtFor(submitted.day);
        }
        await save.mutateAsync({ id: update.id, ...input });
        toast.success("Update saved", submitted.title);
      } else {
        input.published_at = publishedAtFor(submitted.day);
        if (isSuperadmin && submitted.audience !== EVERYONE) input.tenant_id = Number(submitted.audience);
        await create.mutateAsync(input);
        toast.success("Update posted", submitted.title);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the update.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{update ? "Edit update" : "Post an update"}</DialogTitle>
          <DialogDescription>
            {isSuperadmin
              ? "What changed in the console, for the team to read on System updates. It never goes to anybody's notifications."
              : "What changed in how your team works, for them to read on System updates. Only your organisation sees it."}
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field label="Kind" htmlFor="system-update-kind" required>
              <Select
                value={values.kind}
                onValueChange={(v) =>
                  form.setValue("kind", v as SystemUpdateKind, { shouldDirty: true })
                }
              >
                <SelectTrigger id="system-update-kind" className="sm:w-60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k.kind} value={k.kind}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Title" htmlFor="system-update-title" required error={errors.title?.message}>
              <Input
                id="system-update-title"
                autoComplete="off"
                placeholder="Contacts can now be sorted by stage"
                maxLength={TITLE_MAX}
                invalid={Boolean(errors.title)}
                {...form.register("title")}
              />
            </Field>
            <Field
              label="Description"
              htmlFor="system-update-body"
              required
              error={errors.body?.message}
              hint="A line or two: what changed, and where to find it."
            >
              <Textarea
                id="system-update-body"
                rows={4}
                maxLength={BODY_MAX}
                className="font-sans text-sm"
                invalid={Boolean(errors.body)}
                {...form.register("body")}
              />
            </Field>
            <Field label="Date" htmlFor="system-update-day" required error={errors.day?.message}>
              <Input
                id="system-update-day"
                type="date"
                className="w-44"
                invalid={Boolean(errors.day)}
                {...form.register("day")}
              />
            </Field>
            {isSuperadmin && !update && (
              <Field
                label="Who sees it"
                htmlFor="system-update-audience"
                hint="Every tenant, unless it is about one organisation only."
              >
                <Select
                  value={values.audience}
                  onValueChange={(v) => form.setValue("audience", v, { shouldDirty: true })}
                >
                  <SelectTrigger id="system-update-audience" className="sm:w-60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={EVERYONE}>Every tenant</SelectItem>
                    {tenants?.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending || save.isPending}>
              {update ? "Save" : "Post update"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
